import { supabase } from '../lib/supabase';
import type { User } from '../types';
import { auditLogger } from './auditLogger';
import { logger } from '../lib/logger';

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface SignUpData {
  email: string;
  password: string;
  nom: string;
  prenom: string;
  role?: string;
  telephone?: string;
  poste?: string;
}

export interface ResetPasswordData {
  email: string;
}

export interface UpdatePasswordData {
  password: string;
}

/**
 * Détecte le type d'appareil à partir du user agent
 */
const detectDeviceType = (userAgent: string): string => {
  const ua = userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablette';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile|wpdesktop/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

/**
 * Parse le user agent pour extraire le navigateur et l'OS
 */
const parseUserAgent = (userAgent: string): { browser: string; os: string; device: string } => {
  const ua = userAgent;
  let browser = 'Inconnu';
  let os = 'Inconnu';
  const device = detectDeviceType(ua);

  // Détection du navigateur
  if (ua.includes('Edg/')) {
    browser = 'Edge';
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    browser = 'Chrome';
  } else if (ua.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    browser = 'Safari';
  } else if (ua.includes('Opera/') || ua.includes('OPR/')) {
    browser = 'Opera';
  }

  // Détection de l'OS
  if (ua.includes('Windows NT 10.0')) {
    os = 'Windows 10/11';
  } else if (ua.includes('Windows NT')) {
    os = 'Windows';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
  }

  return { browser, os, device };
};

/**
 * L'adresse IP réelle est enregistrée côté serveur (Edge Functions).
 * Côté client, on ne récupère pas l'IP pour éviter les fuites RGPD
 * et les données falsifiables via VPN.
 */
const getClientIP = (): string => 'Enregistrée côté serveur';

/**
 * Connexion avec email et mot de passe
 */
export const signIn = async (credentials: SignInCredentials) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    if (error) {
      logger.error('Erreur Supabase Auth:', error);
      // Traduire les messages d'erreur Supabase
      if (error.message.includes('Invalid login credentials') || error.message.includes('invalid_credentials')) {
        throw new Error('Email ou mot de passe incorrect');
      } else if (error.message.includes('Email not confirmed')) {
        throw new Error('Votre adresse email n\'a pas encore été confirmée. Vérifiez votre boîte mail.');
      } else if (error.message.includes('Too many requests')) {
        throw new Error('Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.');
      } else if (error.message.includes('User not found')) {
        throw new Error('Aucun compte trouvé avec cet email.');
      } else {
        throw new Error(`Erreur de connexion : ${error.message}`);
      }
    }

    // Récupérer les infos utilisateur depuis la table users
    if (data.user) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, nom, prenom, role, organisation_id, statut, telephone, poste, two_factor_enabled, two_factor_activated_at, must_change_password, password_changed_at, password_expiry_days, password_history, derniere_connexion')
        .eq('email', credentials.email)
        .maybeSingle();

      if (userError) {
        logger.error('Erreur récupération utilisateur:', userError);
      }

      // Bloquer la connexion si le compte est suspendu ou inactif
      if (userData && userData.statut !== 'actif') {
        await supabase.auth.signOut({ scope: 'local' });
        if (userData.statut === 'suspendu') {
          throw new Error('Votre compte a été suspendu. Veuillez contacter un administrateur.');
        } else {
          throw new Error('Votre compte est désactivé. Veuillez contacter un administrateur.');
        }
      }

      // Mettre à jour derniere_connexion + en_ligne via RPC (SECURITY DEFINER contourne les RLS)
      const now = new Date().toISOString();
      if (userData) {
        void (async () => { try { await supabase.rpc('user_connexion'); } catch (_) {} })();
        userData.derniere_connexion = now;
        (userData as any).en_ligne = true;
      }

      // Enregistrer la connexion dans le journal d'audit (non-bloquant)
      if (userData) {
        const userAgent = navigator.userAgent;
        const { browser, os, device } = parseUserAgent(userAgent);
        const ipAddress = getClientIP();

        auditLogger.log({
          user_id: userData.id,
          action: 'connexion',
          table_name: 'users',
          record_id: userData.id,
          details: {
            email: credentials.email,
            ip_address: ipAddress,
            user_agent: userAgent,
            browser,
            os,
            device,
            timestamp: new Date().toISOString(),
          },
        }).catch(() => { /* ne pas bloquer la connexion si l'audit échoue */ });
      }

      return {
        user: data.user,
        session: data.session,
        userData: userData || null,
      };
    }

    return { user: data.user, session: data.session, userData: null };
  } catch (error: any) {
    logger.error('Erreur signIn:', error);
    throw error;
  }
};

/**
 * Inscription d'un nouvel utilisateur
 */
export const signUp = async (userData: SignUpData) => {
  try {
    // 1. Créer l'utilisateur dans Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
    });

    if (authError) throw authError;

    // 2. Créer l'entrée dans la table users
    if (authData.user) {
      const { error: userError } = await supabase.from('users').insert({
        id: authData.user.id,
        email: userData.email,
        nom: userData.nom,
        prenom: userData.prenom,
        role: userData.role || 'analyste',
        telephone: userData.telephone,
        poste: userData.poste,
        statut: 'actif',
      });

      if (userError) {
        logger.error('Erreur création utilisateur:', userError);
        throw userError;
      }
    }

    return authData;
  } catch (error: any) {
    logger.error('Erreur signUp:', error);
    throw error;
  }
};

/**
 * Déconnexion
 */
export const signOut = async () => {
  // Récupérer l'utilisateur avant déconnexion pour le log
  const { data: { user } } = await supabase.auth.getUser();
  
  const { error } = await supabase.auth.signOut();
  if (error) throw error;

  // Log de déconnexion
  if (user) {
    await auditLogger.logDeconnexion(user.id);
  }
};

/**
 * Déconnexion de TOUTES les sessions (tous appareils) + nettoyage cache
 */
export const signOutAll = async () => {
  // Récupérer l'utilisateur avant déconnexion pour le log
  const { data: { user } } = await supabase.auth.getUser();

  // Déconnexion locale uniquement (ne pas invalider les autres sessions)
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  if (error) throw error;

  // Log de déconnexion
  if (user) {
    await auditLogger.logDeconnexion(user.id);
  }

  // Nettoyage du cache applicatif (sans toucher aux tokens Supabase)
  clearAllLocalCache();
};

/**
 * Nettoie uniquement le cache applicatif local
 * Ne supprime pas les tokens Supabase (clés préfixées 'sb-')
 */
export const clearAllLocalCache = () => {
  // Préfixes de clés persistantes (préférences utilisateur à conserver entre sessions)
  const PERSISTENT_PREFIXES = [
    'sb-',               // tokens Supabase
    'sidebar_favorites_', // favoris de navigation par utilisateur
  ];

  // Supprimer les clés applicatives dans localStorage en préservant les tokens Supabase et les préférences persistantes
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !PERSISTENT_PREFIXES.some(prefix => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
  // Nettoyer sessionStorage entièrement (données temporaires)
  try { sessionStorage.clear(); } catch { /* ignore */ }
};

/**
 * Demande de réinitialisation de mot de passe
 */
export const resetPassword = async (data: ResetPasswordData) => {
  const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;

  // Récupérer l'utilisateur pour le log
  const { data: user } = await supabase
    .from('users')
    .select('id')
    .eq('email', data.email)
    .maybeSingle();

  // Log de demande de réinitialisation
  if (user) {
    await auditLogger.log({
      user_id: user.id,
      action: 'reinitialisation_mot_de_passe',
      table_name: 'users',
      record_id: user.id,
      details: {
        email: data.email,
        type: 'demande_reinitialisation',
        timestamp: new Date().toISOString()
      }
    });
  }
};

/**
 * Mise à jour du mot de passe
 */
export const updatePassword = async (data: UpdatePasswordData) => {
  // Récupérer l'utilisateur courant
  const { data: { user } } = await supabase.auth.getUser();

  const { error } = await supabase.auth.updateUser({
    password: data.password,
  });
  if (error) throw error;

  // Log du changement de mot de passe
  if (user) {
    await auditLogger.logChangementMotDePasse(user.id, {
      type: 'changement_volontaire',
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Récupérer la session courante
 */
export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
};

/**
 * Récupérer l'utilisateur courant
 */
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  
  if (user) {
    // Récupérer les infos complètes depuis la table users
    const { data: userData } = await supabase
      .from('users')
      .select('id, email, nom, prenom, role, organisation_id, statut, telephone, poste, two_factor_enabled, two_factor_activated_at, must_change_password, password_changed_at, password_expiry_days, password_history, derniere_connexion')
      .eq('id', user.id)
      .maybeSingle();
    
    return { user, userData };
  }
  
  return { user: null, userData: null };
};

/**
 * Récupérer la session courante avec les données utilisateur
 */
export const getCurrentSession = async (): Promise<{ user: User | null }> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) throw error;
    
    if (!session?.user) {
      return { user: null };
    }

    // Récupérer les infos complètes depuis la table users
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, email, nom, prenom, role, organisation_id, statut, telephone, poste, two_factor_enabled, two_factor_activated_at, must_change_password, password_changed_at, password_expiry_days, password_history, derniere_connexion')
      .eq('id', session.user.id)
      .maybeSingle();

    if (userError) {
      logger.error('Erreur récupération utilisateur:', userError);
      return { user: null };
    }

    return { user: userData as User };
  } catch (error) {
    logger.error('Erreur getCurrentSession:', error);
    return { user: null };
  }
};

/**
 * Écouter les changements d'état d'authentification
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return supabase.auth.onAuthStateChange(async (_event, session) => {
    if (session?.user) {
      // Récupérer les infos complètes depuis la table users
      const { data: userData } = await supabase
        .from('users')
        .select('id, email, nom, prenom, role, organisation_id, statut, telephone, poste, two_factor_enabled, two_factor_activated_at, must_change_password, password_changed_at, password_expiry_days, password_history, derniere_connexion')
        .eq('id', session.user.id)
        .maybeSingle();

      callback(userData as User || null);
    } else {
      callback(null);
    }
  });
};
